import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import { JournalEntry, JournalLineItem, ChartOfAccount } from "../types";
import { canCreateJournalEntry, canAdjustClosedPeriod } from "../shared/utils/authorization";
import { getFiscalPeriodForDate, FiscalPeriod } from "../shared/utils/transactionActions";

// Journal entry form data interface
export interface JournalEntryFormData {
  id?: string;
  entryNumber: string;
  entryDate: string; // YYYY-MM-DD
  description: string;
  reference?: string;
  status: "draft" | "posted" | "reversed";
  lineItems: JournalLineItemFormData[];
}

export interface JournalLineItemFormData {
  id?: string;
  accountId: string;
  accountName?: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
}

class JournalEntriesStore {
  rootStore: RootStore;

  // Observables - Only UI state
  selectedEntryId: string | null = null;
  formData: JournalEntryFormData;
  isEditMode = false;
  filterStatus: "all" | "draft" | "posted" | "reversed" = "all";
  searchQuery = "";
  dateRange = {
    start: "",
    end: "",
  };

  // Prior Period State
  priorPeriodWarningDialogOpen = false;
  priorPeriodWarnings: string[] = [];
  pendingPriorPeriodSave = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.formData = this.getInitialFormData();

    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Actions
  setFilterStatus = (status: "all" | "draft" | "posted" | "reversed") => {
    this.filterStatus = status;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setDateRange = (start: string, end: string) => {
    this.dateRange = { start, end };
  };

  selectEntry = (entryId: string) => {
    this.selectedEntryId = entryId;
  };

  openCreateForm = () => {
    this.formData = this.getInitialFormData();
    this.isEditMode = false;
    this.selectedEntryId = null;
  };

  openEditForm = (entryId: string) => {
    const entry = this.journalEntriesStore.entities.find(
      (e: any) => e.id === entryId
    ) as unknown as JournalEntry;
    if (!entry) return;

    // Only allow editing draft entries
    if (entry.status !== "draft") {
      alert("Only draft journal entries can be edited.");
      return;
    }

    this.formData = {
      id: entry.id,
      entryNumber: entry.entryNumber,
      entryDate: entry.entryDate,
      description: entry.description,
      reference: entry.reference,
      status: entry.status,
      lineItems: entry.lineItems.map((item) => ({
        id: item.id,
        accountId: item.accountId,
        accountName: item.accountName,
        description: item.description,
        debitAmount: item.debitAmount,
        creditAmount: item.creditAmount,
      })),
    };
    this.isEditMode = true;
    this.selectedEntryId = entryId;
  };

  closeForm = () => {
    this.isEditMode = false;
    this.selectedEntryId = null;
    this.formData = this.getInitialFormData();
  };

  updateFormData = (
    field: keyof JournalEntryFormData,
    value: string | JournalLineItemFormData[]
  ) => {
    this.formData = { ...this.formData, [field]: value };
  };

  // Line Item Management
  addLineItem = () => {
    const newLineItem: JournalLineItemFormData = {
      accountId: "",
      description: "",
      debitAmount: 0,
      creditAmount: 0,
    };
    this.formData.lineItems = [...this.formData.lineItems, newLineItem];
  };

  updateLineItem = (
    index: number,
    field: keyof JournalLineItemFormData,
    value: string | number
  ) => {
    const updatedLineItems = [...this.formData.lineItems];

    if (field === "accountId") {
      // Update account name when account is selected
      const account = this.accounts.find((a) => a.id === value);
      updatedLineItems[index] = {
        ...updatedLineItems[index],
        accountId: String(value),
        accountName: account?.accountName || "",
      };
    } else {
      updatedLineItems[index] = {
        ...updatedLineItems[index],
        [field]: value,
      };
    }

    this.formData.lineItems = updatedLineItems;
  };

  removeLineItem = (index: number) => {
    this.formData.lineItems = this.formData.lineItems.filter(
      (_, i) => i !== index
    );
  };

  saveJournalEntry = async () => {
    try {
      // Validate double-entry
      if (!this.isJournalEntryValid) {
        throw new Error(
          "Journal entry is not balanced. Total debits must equal total credits."
        );
      }

      // Prepare data for submission
      const submitData = {
        ...this.formData,
        totalDebit: this.totalDebit,
        totalCredit: this.totalCredit,
      };

      if (this.isEditMode && this.formData.id) {
        await this.journalEntriesStore.patch(Number(this.formData.id), submitData);
      } else {
        await this.journalEntriesStore.create(submitData);
      }

      this.closeForm();
    } catch (error) {
      throw error;
    }
  };

  postJournalEntry = async (entryId: string) => {
    try {
      const entry = this.journalEntries.find((e) => e.id === entryId);
      if (!entry) return;

      if (entry.status !== "draft") {
        throw new Error("Only draft entries can be posted.");
      }

      // Validate that entry is balanced
      if (entry.totalDebit !== entry.totalCredit) {
        throw new Error("Cannot post unbalanced journal entry.");
      }

      await this.journalEntriesStore.patch(Number(entryId), {
        status: "posted",
      });

      // TODO: Update account balances based on line items
      // This would typically be done on the backend
    } catch (error) {
      throw error;
    }
  };

  reverseJournalEntry = async (entryId: string) => {
    try {
      const entry = this.journalEntries.find((e) => e.id === entryId);
      if (!entry) return;

      if (entry.status !== "posted") {
        throw new Error("Only posted entries can be reversed.");
      }

      // Create a reversing entry
      const reversingEntry: Partial<JournalEntryFormData> = {
        entryNumber: `REV-${entry.entryNumber}`,
        entryDate: new Date().toISOString().split("T")[0],
        description: `REVERSAL: ${entry.description}`,
        reference: entry.id,
        status: "posted",
        lineItems: entry.lineItems.map((item) => ({
          accountId: item.accountId,
          accountName: item.accountName,
          description: `REVERSAL: ${item.description}`,
          debitAmount: item.creditAmount, // Flip debit and credit
          creditAmount: item.debitAmount,
        })),
      };

      await this.journalEntriesStore.create(reversingEntry);

      // Mark original entry as reversed
      await this.journalEntriesStore.patch(Number(entryId), {
        status: "reversed",
      });
    } catch (error) {
      throw error;
    }
  };

  deleteJournalEntry = async (entryId: string) => {
    try {
      const entry = this.journalEntries.find((e) => e.id === entryId);
      if (entry && entry.status !== "draft") {
        throw new Error("Only draft entries can be deleted.");
      }

      await this.journalEntriesStore.remove(Number(entryId));
    } catch (error) {
      throw error;
    }
  };

  loadJournalEntries = async () => {
    try {
      await this.journalEntriesStore.find({ query: { $limit: 1000, $sort: { entryDate: -1 } } });
    } catch (error) {
      throw error;
    }
  };

  loadAccounts = async () => {
    try {
      await this.chartOfAccountsStore.find({ query: { $limit: 1000 } });
    } catch (error) {
      throw error;
    }
  };

  private getInitialFormData(): JournalEntryFormData {
    const today = new Date().toISOString().split("T")[0];
    const entryNumber = `JE-${Date.now()}`;

    return {
      entryNumber,
      entryDate: today,
      description: "",
      reference: "",
      status: "draft",
      lineItems: [
        { accountId: "", description: "", debitAmount: 0, creditAmount: 0 },
        { accountId: "", description: "", debitAmount: 0, creditAmount: 0 },
      ],
    };
  }

  // Computed values
  get journalEntriesStore() {
    return this.rootStore.journalEntriesStore as ServiceStore;
  }

  get chartOfAccountsStore() {
    return (this.rootStore as any).glAccountsStore as ServiceStore;
  }

  get journalEntries(): JournalEntry[] {
    return (
      (this.journalEntriesStore.entities as unknown as JournalEntry[]) || []
    );
  }

  get accounts(): ChartOfAccount[] {
    return (
      (this.chartOfAccountsStore.entities as unknown as ChartOfAccount[]) || []
    );
  }

  get activeAccounts(): ChartOfAccount[] {
    return this.accounts.filter((a) => a.isActive);
  }

  get selectedEntry(): JournalEntry | null {
    return this.selectedEntryId
      ? (this.journalEntriesStore.entities.find(
          (e: any) => e.id === this.selectedEntryId
        ) as unknown as JournalEntry)
      : null;
  }

  get isLoading(): boolean {
    return this.journalEntriesStore.isServicePending;
  }

  get filteredEntries(): JournalEntry[] {
    let filtered = this.journalEntries;

    // Filter by status
    if (this.filterStatus !== "all") {
      filtered = filtered.filter((entry) => entry.status === this.filterStatus);
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (entry) =>
          entry.entryNumber.toLowerCase().includes(query) ||
          entry.description.toLowerCase().includes(query) ||
          entry.reference?.toLowerCase().includes(query)
      );
    }

    // Filter by date range
    if (this.dateRange.start) {
      filtered = filtered.filter(
        (entry) => entry.entryDate >= this.dateRange.start
      );
    }

    if (this.dateRange.end) {
      filtered = filtered.filter(
        (entry) => entry.entryDate <= this.dateRange.end
      );
    }

    return filtered;
  }

  // Journal Entry Validation
  get totalDebit(): number {
    return this.formData.lineItems.reduce(
      (sum, item) => sum + (item.debitAmount || 0),
      0
    );
  }

  get totalCredit(): number {
    return this.formData.lineItems.reduce(
      (sum, item) => sum + (item.creditAmount || 0),
      0
    );
  }

  get isBalanced(): boolean {
    // Round to 2 decimal places to avoid floating point issues
    const debit = Math.round(this.totalDebit * 100) / 100;
    const credit = Math.round(this.totalCredit * 100) / 100;
    return debit === credit && debit > 0;
  }

  get isJournalEntryValid(): boolean {
    // Must have entry number and description
    if (!this.formData.entryNumber || !this.formData.description) {
      return false;
    }

    // Must have at least 2 line items
    if (this.formData.lineItems.length < 2) {
      return false;
    }

    // All line items must have an account selected
    const hasInvalidLineItems = this.formData.lineItems.some(
      (item) => !item.accountId
    );
    if (hasInvalidLineItems) {
      return false;
    }

    // Must be balanced
    if (!this.isBalanced) {
      return false;
    }

    return true;
  }

  // Summary Statistics
  get entrySummary() {
    const draft = this.journalEntries.filter((e) => e.status === "draft")
      .length;
    const posted = this.journalEntries.filter((e) => e.status === "posted")
      .length;
    const reversed = this.journalEntries.filter((e) => e.status === "reversed")
      .length;
    const totalEntries = this.journalEntries.length;

    return {
      draft,
      posted,
      reversed,
      totalEntries,
    };
  }

  // ==================== Access Control ====================

  /**
   * Check if the current user can create/edit journal entries
   * Only authorized users (Brandi, Carrie, Clay) can create journal entries
   */
  get canCreate(): boolean {
    const userStore = this.rootStore.userStore as any;
    const currentUser = userStore?.currentUser || userStore?.user;
    return canCreateJournalEntry(currentUser);
  }

  /**
   * Check if the current user can edit a specific entry
   * Must be authorized AND entry must be in draft status
   */
  canEdit(entry: JournalEntry): boolean {
    return this.canCreate && entry.status === "draft";
  }

  // ==================== Prior Period Support ====================

  /**
   * Get current user for authorization checks
   */
  get currentUser() {
    const userStore = this.rootStore.userStore as any;
    return userStore?.currentUser || userStore?.user;
  }

  /**
   * Get fiscal periods from the fiscal periods store
   */
  get fiscalPeriods(): FiscalPeriod[] {
    const fiscalPeriodsStore = (this.rootStore as any).fiscalPeriodsStore;
    return fiscalPeriodsStore?.fiscalPeriods || [];
  }

  /**
   * Get the fiscal period for the current entry date
   */
  get entryFiscalPeriod(): FiscalPeriod | null {
    if (!this.formData.entryDate) return null;
    return getFiscalPeriodForDate(this.formData.entryDate, this.fiscalPeriods);
  }

  /**
   * Check if the entry date is in a prior (closed) period
   */
  get isPriorPeriodEntry(): boolean {
    const period = this.entryFiscalPeriod;
    return period !== null && period.status === "closed";
  }

  /**
   * Check if entry date is in a locked period (cannot be modified)
   */
  get isLockedPeriodEntry(): boolean {
    const period = this.entryFiscalPeriod;
    return period !== null && period.status === "locked";
  }

  /**
   * Check if user can adjust closed periods
   */
  get canAdjustPriorPeriod(): boolean {
    return canAdjustClosedPeriod(this.currentUser);
  }

  /**
   * Get warnings for prior period entry
   */
  get priorPeriodEntryWarnings(): string[] {
    const warnings: string[] = [];
    const period = this.entryFiscalPeriod;

    if (!period) return warnings;

    if (period.status === "locked") {
      warnings.push(`The fiscal period ${period.month}/${period.year} is LOCKED and cannot be modified.`);
      warnings.push("Contact your system administrator if you need to make changes.");
      return warnings;
    }

    if (period.status === "closed") {
      warnings.push(`The fiscal period ${period.month}/${period.year} is CLOSED.`);

      if (this.canAdjustPriorPeriod) {
        warnings.push("As an authorized user, you can post entries to closed periods.");
        warnings.push("This entry will be flagged as a prior period adjustment.");
        if ((period as any).closedByName && period.closedAt) {
          warnings.push(`Period was closed by ${(period as any).closedByName} on ${new Date(period.closedAt).toLocaleDateString()}.`);
        }
      } else {
        warnings.push("You do not have permission to post entries to closed periods.");
        warnings.push("Please contact Brandi Clark, Carrie, or Clay to make this adjustment.");
      }
    }

    return warnings;
  }

  /**
   * Open prior period warning dialog
   */
  openPriorPeriodWarningDialog = () => {
    this.priorPeriodWarnings = this.priorPeriodEntryWarnings;
    this.priorPeriodWarningDialogOpen = true;
  };

  /**
   * Close prior period warning dialog
   */
  closePriorPeriodWarningDialog = () => {
    this.priorPeriodWarningDialogOpen = false;
    this.priorPeriodWarnings = [];
    this.pendingPriorPeriodSave = false;
  };

  /**
   * Confirm and save prior period entry
   */
  confirmPriorPeriodSave = async () => {
    this.pendingPriorPeriodSave = true;
    try {
      await this.executeSaveJournalEntry(true);
      this.closePriorPeriodWarningDialog();
    } catch (error) {
      throw error;
    } finally {
      runInAction(() => {
        this.pendingPriorPeriodSave = false;
      });
    }
  };

  /**
   * Check entry date and either save directly or show warning
   */
  saveJournalEntryWithPeriodCheck = async () => {
    // Check if entry date is in a closed or locked period
    if (this.isLockedPeriodEntry) {
      throw new Error("Cannot save entry to a locked fiscal period.");
    }

    if (this.isPriorPeriodEntry) {
      if (!this.canAdjustPriorPeriod) {
        throw new Error("You do not have permission to post entries to closed periods.");
      }
      // Show warning dialog for prior period entries
      this.openPriorPeriodWarningDialog();
      return;
    }

    // No period restrictions, save directly
    await this.executeSaveJournalEntry(false);
  };

  /**
   * Execute the actual save operation
   */
  private executeSaveJournalEntry = async (isPriorPeriodAdjustment: boolean) => {
    try {
      // Validate double-entry
      if (!this.isJournalEntryValid) {
        throw new Error(
          "Journal entry is not balanced. Total debits must equal total credits."
        );
      }

      // Prepare data for submission
      const submitData = {
        ...this.formData,
        totalDebit: this.totalDebit,
        totalCredit: this.totalCredit,
        isPriorPeriodAdjustment,
        adjustedBy: isPriorPeriodAdjustment ? this.currentUser?.username : undefined,
        adjustedAt: isPriorPeriodAdjustment ? new Date().toISOString() : undefined,
      };

      if (this.isEditMode && this.formData.id) {
        await this.journalEntriesStore.patch(Number(this.formData.id), submitData);
      } else {
        await this.journalEntriesStore.create(submitData);
      }

      this.closeForm();
    } catch (error) {
      throw error;
    }
  };
}

export default JournalEntriesStore;
